import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { HomeIcon, PlusCircle, CheckCircle, CircleDollarSign, User, PieChart, Users, List, Clock, AlertCircle, Eye, TrendingUp, Phone, Mail, FileText, MessageSquare, CheckSquare } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import StatsBar from "@/components/common/StatsBar";
import DashboardTile from "@/components/common/DashboardTile";
import SellerRestrictedDashboardTile from "@/components/seller/SellerRestrictedDashboardTile";
import ShareListingsButton from "@/components/dashboard/ShareListingsButton";
import { SellerShareButtons } from "@/components/common/SocialShareButtons";
import { useAuth } from "@/contexts/AuthContext";
import { useDashboardMetrics } from "@/hooks/useDashboardMetrics";
import { useRecentActivity } from "@/hooks/useRecentActivity";
import { useBuyerLists } from "@/hooks/useBuyerLists";
import TourWrapper from "@/components/onboarding/TourWrapper";
import { sellerDashboardSteps } from "@/config/tours/sellerDashboardTour";
import { safeStorage } from "@/utils/safariStorage";

const getActivityConfig = (activityType: string, iconType?: string) => {
  switch (activityType) {
    case 'created':
      return { color: '#ffd700', icon: PlusCircle };
    case 'view':
      return { color: '#3b82f6', icon: Eye };
    case 'status_change':
      return { color: '#22c55e', icon: TrendingUp };
    case 'task_created':
      return { color: '#a855f7', icon: CheckSquare };
    case 'activity_logged': {
      const iconMap: Record<string, any> = {
        phone_call: Phone,
        email: Mail,
        meeting: Users,
        text_message: MessageSquare,
        note: FileText,
      };
      return { color: '#06b6d4', icon: iconMap[iconType || ''] || FileText };
    }
    default:
      return { color: '#6b7280', icon: Clock };
  }
};



const DashboardPage = () => {
  // Get user profile from auth context
  const { profile } = useAuth();
  const location = useLocation();
  const [runTour, setRunTour] = useState(false);
  
  // Get buyer lists data
  const { lists } = useBuyerLists();
  
  // Get real-time metrics data
  const { metrics, isLoading, error } = useDashboardMetrics();
  
  // Get real-time activity data
  const { activities, isLoading: activitiesLoading, error: activitiesError } = useRecentActivity();
  
  useEffect(() => {
    // Don't start tour until profile is loaded
    if (!profile) return;
    
    // Check if triggered from Help Center
    if (location.state?.startTour) {
      setRunTour(true);
      // Clear the state so refresh doesn't re-trigger
      window.history.replaceState({}, document.title);
      return;
    }
    
    // Auto-start for first-time users
    const hasSeenTour = safeStorage.getItem('godyspo_tour_seller_dashboard');
    if (hasSeenTour) return;
    
    let timeoutId: NodeJS.Timeout;
    
    // Polling function to check for DOM elements
    const checkAndStartTour = (attempts = 0) => {
      const maxAttempts = 20; // 4 seconds total (20 * 200ms)
      const firstTarget = document.querySelector('[data-tour="upload-deal"]');
      
      if (firstTarget) {
        setRunTour(true);
      } else if (attempts < maxAttempts) {
        timeoutId = setTimeout(() => checkAndStartTour(attempts + 1), 200);
      }
    };
    
    // Initial delay then start checking
    timeoutId = setTimeout(() => checkAndStartTour(0), 500);
    
    return () => clearTimeout(timeoutId);
  }, [location.state, profile]);

  const handleTourComplete = () => {
    safeStorage.setItem('godyspo_tour_seller_dashboard', 'true');
    setRunTour(false);
  };
  
  return (
    <>
      <TourWrapper
        steps={sellerDashboardSteps}
        run={runTour}
        onComplete={handleTourComplete}
        onSkip={handleTourComplete}
      />
      
      <div className="space-y-6 animate-fade-in-stable">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Welcome, {profile?.full_name || "User"}
          </h1>
          <p className="text-muted-foreground">Here's an overview of your real estate deals</p>
        </div>
        <div className="flex flex-col sm:items-end gap-2">
          <ShareListingsButton />
          {(profile?.profile_slug || profile?.id) && (
            <SellerShareButtons
              sellerParam={profile.profile_slug || profile.id}
              sellerName={profile.full_name}
              label="Post to:"
            />
          )}
        </div>
      </div>
      
      {/* Main metrics */}
      <div data-tour="stats-bar">
        <StatsBar
        stats={[
          {
            title: "Active Deals",
            value: metrics.activeDeals,
            icon: <CheckCircle className="h-5 w-5" />,
            description: "Current active listings"
          },
          {
            title: "Pending Deals",
            value: metrics.pendingDeals,
            icon: <Clock className="h-5 w-5" />,
            description: "Deals awaiting close"
          },
          {
            title: "Closed Deals",
            value: metrics.closedDeals,
            icon: <CircleDollarSign className="h-5 w-5" />,
            description: "Total deals closed"
          },
          {
            title: "Expired Deals",
            value: metrics.expiredDeals,
            icon: <AlertCircle className="h-5 w-5" />,
            description: "Contracts expired"
          },
          {
            title: "Property Views",
            value: metrics.propertyViews,
            icon: <Eye className="h-5 w-5" />,
            description: "Unique visitors"
          },
          {
            title: "Conversion Rate",
            value: metrics.conversionRate,
            icon: <TrendingUp className="h-5 w-5" />,
            description: "Closed to total"
          }
        ]}
        isLoading={isLoading}
      />
      </div>
      
      {/* Main tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <div className="md:col-span-2 lg:col-span-1">
          <SellerRestrictedDashboardTile
            title="Upload New Deal"
            description="Create a new property listing to market to investors"
            icon={<PlusCircle className="h-6 w-6" />}
            to="/seller/properties/new"
            isPrimary={true}
            isRestricted={true}
            tourId="upload-deal"
          />
        </div>
        
        <DashboardTile
          title="Active Deals"
          description="View and manage your current property listings"
          icon={<CheckCircle className="h-6 w-6" />}
          to="/seller/properties/active"
          count={isLoading ? undefined : metrics.activeDeals}
          tourId="active-deals"
        />
        
        <SellerRestrictedDashboardTile
          title="Search Buyers"
          description="Find qualified investors for your deals"
          icon={<Users className="h-6 w-6" />}
          to="/seller/search-buyers"
          isRestricted={true}
          tourId="search-buyers"
        />
        
        <SellerRestrictedDashboardTile
          title="My Buyer Lists"
          description="Manage your saved buyer contacts"
          icon={<List className="h-6 w-6" />}
          to="/seller/buyer-lists"
          count={lists.length}
          isRestricted={true}
        />
        
        <DashboardTile
          title="Closed Deals"
          description="View your successfully closed property deals"
          icon={<CircleDollarSign className="h-6 w-6" />}
          to="/seller/properties/closed"
          count={isLoading ? undefined : metrics.closedDeals}
        />
        
        <DashboardTile
          title="My Profile"
          description="Manage your account information and settings"
          icon={<User className="h-6 w-6" />}
          to="/seller/profile"
        />
      </div>
      
      {/* Recent activity */}
      <div>
        <Card data-tour="recent-activity">
          <CardHeader>
            <CardTitle className="text-lg">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {activitiesError ? (
              <div className="text-center py-4">
                <p className="text-muted-foreground">Unable to load recent activity</p>
              </div>
            ) : activitiesLoading ? (
              <div className="text-center py-4">
                <p className="text-muted-foreground">Loading recent activity...</p>
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted-foreground">No recent activity yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Create your first property listing to see activity here
                </p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto scrollbar-dark rounded-lg bg-background/50 shadow-[inset_0_2px_6px_rgba(0,0,0,0.3)] p-4">
                <div className="relative">
                  {/* Vertical connecting line */}
                  <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-muted/40" />

                  {activities.map((activity, index) => {
                    const config = getActivityConfig(activity.activity_type, activity.icon_type);
                    return (
                      <div
                        key={activity.id}
                        className="relative flex items-start gap-3 pb-5 last:pb-0 animate-fade-in"
                        style={{ animationDelay: `${index * 80}ms`, animationFillMode: 'both' }}
                      >
                        {/* Dot on the line */}
                        <div className="relative z-10 flex flex-col items-center">
                          <div
                            className="w-[8px] h-[8px] rounded-full mt-3 ring-2 ring-background"
                            style={{ backgroundColor: config.color }}
                          />
                        </div>

                        {/* Icon circle */}
                        <div
                          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1"
                          style={{ backgroundColor: `${config.color}20` }}
                        >
                          <config.icon className="w-4 h-4" style={{ color: config.color }} />
                        </div>

                        {/* Text content */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm leading-tight">{activity.description}</p>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{activity.property_address}</p>
                          <p className="text-xs text-muted-foreground/70 mt-0.5">
                            {activity.formattedDate} — {activity.timeAgo}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
};

export default DashboardPage;
